/**
 * Centralized API client with cookie-based authentication
 * Replaces manual JWT Bearer token authentication with HTTP-only cookies
 */

import { API_BASE_URL } from './apiConfig';
import { isRequiredPasswordChangeLogoutInProgress } from './passwordChangeLogout';

interface FetchOptions extends RequestInit {
  headers?: HeadersInit;
}

/**
 * Makes an authenticated API request with credentials (cookies) included
 * @param endpoint - The API endpoint (e.g., '/api/users/profile')
 * @param options - Fetch options
 * @returns Promise<Response>
 */
export const apiClient = async (
  endpoint: string,
  options: FetchOptions = {}
): Promise<Response> => {
  const url = endpoint.startsWith('http') ? endpoint : `${API_BASE_URL}${endpoint.startsWith('/') ? endpoint : `/${endpoint}`}`;

  const defaultHeaders: HeadersInit = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  };

  const config: RequestInit = {
    ...options,
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
    credentials: 'include', // CRITICAL: This sends HTTP-only cookies automatically
  };

  try {
    const response = await fetch(url, config);

    // Handle 401 Unauthorized - session expired or invalid
    if (response.status === 401 && !isRequiredPasswordChangeLogoutInProgress()) {
      // Clear local storage
      localStorage.removeItem('userId');
      localStorage.removeItem('hasProfile');
      localStorage.removeItem('termsAndConditionsAccepted');
      localStorage.removeItem('loggedInUserId');
      localStorage.removeItem('authProvider');
      localStorage.removeItem('hasLoggedInBefore');

      // Redirect to login
      window.location.href = '/login?session_expired=true';
      throw new Error('Session expired');
    }

    return response;
  } catch (error) {
    // Re-throw network errors
    throw error;
  }
};

/**
 * Helper method for GET requests
 */
export const apiGet = async (endpoint: string, options: FetchOptions = {}): Promise<Response> => {
  return apiClient(endpoint, { ...options, method: 'GET' });
};

/**
 * Helper method for POST requests
 */
export const apiPost = async (
  endpoint: string,
  body?: any,
  options: FetchOptions = {}
): Promise<Response> => {
  return apiClient(endpoint, {
    ...options,
    method: 'POST',
    body: body ? JSON.stringify(body) : undefined,
  });
};

/**
 * Helper method for PUT requests
 */
export const apiPut = async (
  endpoint: string,
  body?: any,
  options: FetchOptions = {}
): Promise<Response> => {
  return apiClient(endpoint, {
    ...options,
    method: 'PUT',
    body: body ? JSON.stringify(body) : undefined,
  });
};

/**
 * Helper method for DELETE requests
 */
export const apiDelete = async (endpoint: string, options: FetchOptions = {}): Promise<Response> => {
  return apiClient(endpoint, { ...options, method: 'DELETE' });
};

